import Lottie from "lottie-react";
import { useEffect, useState } from "react";
import { StoryData } from "../../types";
import ChatBubble from "../ChatBubble/ChatBubble";
import YetiCharacter from "../YetiCharacter/YetiCharacter";
import WebLottiePlayer from "../../../WebStoryScreen/Components/WebLottiePlayer";
import confettiJson from "../../../../appAssets/jsons/confetti.json";
import "./OnBoardingWelcomeTemplate.scss";
import { trackEvent } from "../../../../../mixpanel";
import { TrackingEvents } from "../../../../Utilities/analytics";

interface OnBoardingWelcomeTemplateProps {
  isOnboardingDone?: boolean;
  storyArrayData: StoryData | null;
  updateState: () => void;
}

interface LastBubbleData {
  content: { content?: string; textColor?: string; audio?: string } | null;
  isOwnMessage: boolean;
  textcolor: string;
  audio: string;
}

function OnBoardingWelcomeTemplate({
  storyArrayData,
  updateState,
}: OnBoardingWelcomeTemplateProps) {
  const [objectIndex, setObjectIndex] = useState(0);
  const [conversationIndex, setConversationIndex] = useState(0);
  const [showButton, setShowButton] = useState(false);
  const [showYeti, setShowYeti] = useState(true);
  const [showHomeUrl, setHomeUrl] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [lastBubbleData, setLastBubbleData] = useState<LastBubbleData>({
    content: null,
    isOwnMessage: false,
    textcolor: "",
    audio: "",
  });

  const firstBlock = storyArrayData?.blocks?.[0] ?? null;
  const bgUrl = firstBlock?.bgImageTab || firstBlock?.bgImage || "";

  useEffect(() => {
    const conversations = firstBlock?.conversations ?? [];
    if (conversationIndex > conversations.length - 1) return;

    // Extract home URL from blockObjects
    firstBlock?.conversations?.[conversationIndex]?.object?.[
      objectIndex
    ]?.blockObjects?.forEach((obj: { url?: string }) => {
      if (obj?.url?.toLowerCase()?.includes("home")) {
        setHomeUrl(obj.url ?? "");
      }
    });

    const currentConversation = conversations[conversationIndex]?.object || [];

    if (objectIndex < currentConversation.length) {
      const delay =
        parseInt(
          conversations[conversationIndex]?.object[objectIndex]?.duration ||
            "5000",
          10,
        ) + 2000;
      const timer = setTimeout(() => {
        setShowYeti((prev) => !prev);
        setObjectIndex((prev) => prev + 1);
      }, delay);
      return () => clearTimeout(timer);
    } else {
      // Save last bubble before advancing conversation
      const lastConversation =
        conversations[conversations.length - 1]?.object || [];
      const lastObj = currentConversation[currentConversation.length - 1]
        ?.content
        ? currentConversation[currentConversation.length - 1]
        : currentConversation[currentConversation.length - 2];
      setLastBubbleData({
        content: lastObj ?? null,
        isOwnMessage:
          lastConversation.length === 1
            ? false
            : lastConversation.length === 2 &&
                (currentConversation[currentConversation.length - 1]?.content
                  ?.length ?? 0) > 0
              ? true
              : false,
        textcolor: lastObj?.textColor || "",
        audio: lastObj?.audio || "",
      });
      setObjectIndex(0);
      setConversationIndex((prev) => prev + 1);
    }

    if (conversationIndex === conversations.length - 1) {
      setShowButton(true);
    }
  }, [conversationIndex, objectIndex, firstBlock]);

  // Conversation index resolved safely (cap at last valid index)
  const conversations = firstBlock?.conversations ?? [];
  const conversationIndexVal = Math.min(
    conversationIndex,
    conversations.length - 1,
  );

  const currentConversation = conversations[conversationIndexVal]?.object || [];
  const currentObject = currentConversation[objectIndex] ?? null;

  const leftCharImage =
    conversations[conversationIndexVal]?.object?.[0]?.characterImage ?? null;
  const rightCharImage =
    conversations[conversationIndexVal]?.object?.[1]?.characterImage ?? null;

  const handleContinue = () => {
    setShowConfetti(true);
    setTimeout(() => {
      setShowConfetti(false);
      updateState();
    }, 2000);
  };

  return (
    <div
      className="welcome-template"
      style={bgUrl ? { backgroundImage: `url(${bgUrl})` } : undefined}
    >
      <div className="welcome-template__stage">
        {/* Chat bubble — top-left, above the yetis */}
        <div className="welcome-template__bubble">
          {showButton && lastBubbleData.content?.content?.trim() ? (
            <ChatBubble
              content={lastBubbleData.content.content}
              textColor={lastBubbleData.textcolor}
              tailSide={lastBubbleData.isOwnMessage ? "right" : "left"}
              audioUri={lastBubbleData.audio}
            />
          ) : currentObject?.content?.trim() ? (
            <ChatBubble
              content={currentObject.content}
              textColor={currentObject.textColor}
              tailSide={
                currentConversation.length === 1
                  ? "left"
                  : showYeti
                    ? "left"
                    : "right"
              }
              audioUri={currentObject.audio}
            />
          ) : null}
        </div>

        {/* Both yetis grouped on the left side */}
        <div className="welcome-template__yetis">
          <div className="welcome-template__yeti welcome-template__yeti--left">
            <YetiCharacter
              characterImage={rightCharImage ?? undefined}
              className="welcome-template__yeti-lottie"
            />
          </div>
          <div className="welcome-template__yeti welcome-template__yeti--right">
            <YetiCharacter
              characterImage={leftCharImage ?? undefined}
              className="welcome-template__yeti-lottie"
            />
          </div>
        </div>

        {/* Home/balloon — right side, tappable Lottie */}
        {showHomeUrl && (
          <button
            type="button"
            className="welcome-template__home-btn"
            onClick={handleContinue}
          >
            <WebLottiePlayer
              source={showHomeUrl}
              className="welcome-template__home-lottie"
              loop
              autoPlay
            />
          </button>
        )}
      </div>

      {/* Continue button — shown when conversations are done */}
      {showButton && (
        <div className="welcome-template__actions">
          <button
            type="button"
            className="welcome-template__btn"
            onClick={() => {
              trackEvent(TrackingEvents.introduction_completed, {
                type: "magicBalloon",
              });
              handleContinue();
            }}
          >
            Continue
          </button>
        </div>
      )}

      {/* Confetti overlay */}
      {showConfetti && (
        <Lottie
          animationData={confettiJson}
          loop
          autoplay
          className="welcome-template__confetti"
        />
      )}
    </div>
  );
}

export default OnBoardingWelcomeTemplate;
